'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { TabsProps } from './types'

import styles from './tabs.module.scss'

const Tabs: React.FC<TabsProps> = ({
	id = '',
	activeKey: externalActiveKey,
	defaultActiveKey,
	onChange,
	onTabClick,
	tabPosition = 'top',
	type = 'line',
	size = 'default',
	centered = false,
	items,
	className = '',
	style,
	destroyInactiveTabPane = false
}) => {
	const [internalActiveKey, setInternalActiveKey] = useState<string>(defaultActiveKey || '')
	const separateLineRef = useRef<HTMLDivElement>(null)
	const tabPanelRef = useRef<HTMLDivElement>(null)
	const tabsNavListRef = useRef<HTMLDivElement>(null)
	const tabsWrapperRef = useRef<HTMLDivElement>(null)
	const shouldScrollToTabsRef = useRef(false)
	const [isStuck, setIsStuck] = useState(false)

	const activeKey = externalActiveKey !== undefined ? externalActiveKey : internalActiveKey

	useEffect(() => {
		if (externalActiveKey === undefined) {
			if (defaultActiveKey) setInternalActiveKey(defaultActiveKey)
			else {
				const firstVisible = items.find(i => !i.hidden)
				if (firstVisible) setInternalActiveKey(firstVisible.key)
			}
		} else {
			const isActiveKeyExists = items.some(tabItem => tabItem.key === externalActiveKey && !tabItem.hidden)
			if (!isActiveKeyExists) {
				onChange(defaultActiveKey)
			}
		}
	}, [externalActiveKey, defaultActiveKey, items])

	useEffect(() => {
		const accent = separateLineRef.current
		const list = tabsNavListRef.current
		const nav = tabPanelRef.current
		if (!accent || !list) return

		const activeTabPane = list.querySelector<HTMLElement>(`#tab-pane-${CSS.escape(activeKey)}`)
		if (!activeTabPane) return

		accent.style.left = `${activeTabPane.offsetLeft}px`
		accent.style.width = `${activeTabPane.offsetWidth}px`

		if (!nav) return

		const navRect = nav.getBoundingClientRect()
		const tabRect = activeTabPane.getBoundingClientRect()
		const overflowLeft = tabRect.left - navRect.left
		const overflowRight = tabRect.right - navRect.right

		if (overflowLeft < 0 || overflowRight > 0) {
			nav.scrollBy({
				left: overflowLeft < 0 ? overflowLeft : overflowRight,
				behavior: 'smooth'
			})
		}
	}, [activeKey, items])

	useEffect(() => {
		const handleScroll = () => {
			if (!tabPanelRef.current) return
			const { top } = tabPanelRef.current.getBoundingClientRect()

			setIsStuck(top <= 175)
		}

		window.addEventListener('scroll', handleScroll)

		return () => window.removeEventListener('scroll', handleScroll)
	}, [])

	useEffect(() => {
		if (!shouldScrollToTabsRef.current || !tabsWrapperRef.current) return

		shouldScrollToTabsRef.current = false

		const scrollToTabs = () => {
			if (!tabsWrapperRef.current) return
			const elementTop = tabsWrapperRef.current.getBoundingClientRect().top + window.scrollY
			const offset = window.innerWidth <= 848 ? 70 : 160

			window.scrollTo({ top: elementTop - offset, behavior: 'smooth' })
		}

		requestAnimationFrame(scrollToTabs)
	}, [activeKey])

	const handleTabClick = useCallback(
		(key: string, e: React.MouseEvent) => {
			const tab = items.find(t => t.key === key)
			if (tab?.disabled) return

			if (externalActiveKey === undefined) setInternalActiveKey(key)
			onTabClick?.(key, e)
			onChange?.(key)
			shouldScrollToTabsRef.current = true
		},
		[items, externalActiveKey, onTabClick, onChange]
	)

	const navClasses = [
		styles.tabsNav,
		styles[`nav-${tabPosition}`],
		styles[`nav-${type}`],
		styles[`nav-size-${size}`],
		styles[`nav-size-${size}`],
		centered ? styles.navCentered : '',
		isStuck ? styles.tabsNav__stuck : ''
	].join(' ')

	const wrapperClasses = [styles.tabs, styles[`tabs-${tabPosition}`], styles[`tabs-${type}`], className].join(' ')

	const visibleTabs = useMemo(() => items.filter(i => !i.hidden), [items])

	return (
		<div ref={tabsWrapperRef} className={wrapperClasses} style={style} id={id} role="tablist">
			<div ref={tabPanelRef} className={navClasses}>
				<div className={styles.tabsNavWrapper}>
					<div ref={tabsNavListRef} className={styles.tabsNav__list}>
						{visibleTabs.map(tab => {
							const tabClasses = [
								styles.tab,
								tab.key === activeKey ? styles['tab--active'] : '',
								tab.disabled ? styles.tabDisabled : ''
							].join(' ')

							return (
								<div
									id={'tab-pane-' + tab.key}
									key={tab.key}
									className={tabClasses}
									onClick={e => handleTabClick(tab.key, e)}
									role="tab"
									data-node-key={tab.key}
									aria-selected={tab.key === activeKey}
									aria-disabled={tab.disabled}>
									<span className={styles.tabLabel}>{tab.tab}</span>
								</div>
							)
						})}
						<div className={styles.tab_accent_line} ref={separateLineRef} />
					</div>
				</div>
			</div>

			<div className={styles.tabsContent}>
				{items.map(tab => {
					if (tab.hidden) return null

					const isActive = tab.key === activeKey

					if (isActive) return <div key={tab.key}>{tab.content}</div>

					if (tab.forceRender && !destroyInactiveTabPane) {
						return (
							<div key={tab.key} style={{ display: 'none' }} aria-hidden>
								{tab.content}
							</div>
						)
					}

					return null
				})}
			</div>
		</div>
	)
}

export { Tabs }
